import { LogOut, User as UserIcon } from 'lucide-react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { User } from '@/api/auth';
import { authApi, type LoginCredentials } from '@/api/auth';
import { getSessionErrorMessage } from '@/lib/auth-session';

interface NavBarItem {
  name: string;
  onClick?: () => void;
  href?: string;
  icon?: React.ReactNode;
}

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionError: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  navBarItems: NavBarItem[];
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const checkAuth = useCallback(async () => {
    setSessionError(null);
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const userData = await authApi.me();
      if (localStorage.getItem('token')) setUser(userData);
    } catch (error) {
      if (localStorage.getItem('token')) {
        setSessionError(getSessionErrorMessage(error));
      } else {
        setUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = async (credentials: LoginCredentials) => {
    const response = await authApi.login(credentials);
    localStorage.setItem('token', response.token);
    if (response.bwauthTokens?.refreshToken) {
      localStorage.setItem('refreshToken', response.bwauthTokens.refreshToken);
    } else {
      localStorage.removeItem('refreshToken');
    }
    setUser(response.user);
    setSessionError(null);
  };

  const logout = useCallback(() => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    authApi.logout({ token, refreshToken }).catch(() => {
      // Ignore logout API errors
    });
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setSessionError(null);
  }, []);

  const navBarItems: NavBarItem[] = useMemo(
    () => [
      {
        name: 'Perfil',
        href: '/admin/user/profile',
        icon: <UserIcon className="h-5 w-5 shrink-0 text-gray-400" />,
      },
      {
        name: 'Sair',
        onClick: logout,
        icon: <LogOut className="h-5 w-5 shrink-0 text-gray-400" />,
      },
    ],
    [logout],
  );

  // Refresh the current user profile
  const refreshProfile = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        sessionError,
        login,
        logout,
        navBarItems,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
